import { z } from "zod";

const tagSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const resourceItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  quantity: z.number(),
});

export const workforceContractorGroupSchema = z.object({
  id: z.number(),
  name: z.string(),
  workforces: z.array(resourceItemSchema),
});

export const equipmentContractorGroupSchema = z.object({
  id: z.number(),
  name: z.string(),
  equipments: z.array(resourceItemSchema),
});

export const dailyLogRequestSchema = z.object({
  draft_id: z.string(),
  description: z.string(),
  location: z.string(),
  log_date: z.string(),
  weather: z.string(),
  temperature: z.string(),
  temperature_unit: z.literal("F"),
  precipitation: z.string(),
  precipitation_unit: z.literal("MM"),
  process_rate: z.string(),
  tags: z.array(tagSchema),
  workforces: z.array(resourceItemSchema),
  equipments: z.array(resourceItemSchema),
  image_urls: z.array(z.string()),
});

export const dailyLogFormSchema = z.object({
  location: z.string(),
  description: z.string(),
  date: z.string(),
  weather: z.string(),
  temperature: z.number(),
  precipitation: z.number(),
  processRate: z.number(),
  tags: z.array(tagSchema),
  workforce_contractors: z.array(workforceContractorGroupSchema),
  equipment_contractors: z.array(equipmentContractorGroupSchema),
  image_urls: z.array(z.string()),
});

export const dailyLogFormToRequestSchema = dailyLogFormSchema.transform((form) => ({
  description: form.description,
  location: form.location,
  log_date: form.date,
  weather: form.weather,
  temperature: form.temperature.toString(),
  temperature_unit: "F" as const,
  precipitation: form.precipitation.toString(),
  precipitation_unit: "MM" as const,
  process_rate: form.processRate.toString(),
  tags: form.tags,
  workforces: form.workforce_contractors.flatMap((c) =>
    c.workforces.map((w) => ({ contractor_id: c.id, trade_id: Number(w.id), quantity: w.quantity })),
  ),
  equipments: form.equipment_contractors.flatMap((c) =>
    c.equipments.map((e) => ({ contractor_id: c.id, trade_id: Number(e.id), quantity: e.quantity })),
  ),
  image_urls: form.image_urls,
}));

export const projectWeatherSchema = z.object({
  weather: z.string(),
  temperature: z.string(),
  temperature_unit: z.string(),
  precipitation: z.string(),
  precipitation_unit: z.string(),
});

const aiRefineStatusSchema = z.enum(["SUCCESS", "PENDING", "FAILURE"]);

export const aiRefineTaskResponseSchema = z.object({
  task_id: z.string(),
});

export const aiRefineResultSchema = z.object({
  status: aiRefineStatusSchema,
  description: z.string(),
});

export type AIRefineTaskResponse = z.infer<typeof aiRefineTaskResponseSchema>;
export type AIRefineResult = z.infer<typeof aiRefineResultSchema>;
export type AIRefineStatus = z.infer<typeof aiRefineStatusSchema>;

export type DailyLogRequest = z.infer<typeof dailyLogRequestSchema>;
export type DailyLogFormValues = z.infer<typeof dailyLogFormSchema>;
export type DailyLogRequestBody = z.output<typeof dailyLogFormToRequestSchema>;
export type ProjectWeather = z.infer<typeof projectWeatherSchema>;
export type Tag = z.infer<typeof tagSchema>;
export type ResourceItem = z.infer<typeof resourceItemSchema>;
export type WorkforceContractorGroup = z.infer<typeof workforceContractorGroupSchema>;
export type EquipmentContractorGroup = z.infer<typeof equipmentContractorGroupSchema>;
